import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

const API = axios.create({ baseURL: '/api', withCredentials: true });

const CustomerManagement = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', agent_number: '', copy_from_customer_id: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchCustomers(); }, []);

  const fetchCustomers = async () => {
    try {
      const res = await API.get('/customers');
      setCustomers(res.data);
    } catch {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.name) return toast.error('Customer name is required');
    setSaving(true);
    try {
      await API.post('/customers', {
        name: form.name,
        agent_number: form.agent_number || '20343',
        copy_from_customer_id: form.copy_from_customer_id || null
      });
      toast.success(`Customer ${form.name} created`);
      setShowModal(false);
      setForm({ name: '', agent_number: '', copy_from_customer_id: '' });
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create customer');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (customer) => {
    try {
      await API.put(`/customers/${customer.customer_id}`, { is_active: !customer.is_active });
      setCustomers(prev => prev.map(c =>
        c.customer_id === customer.customer_id ? { ...c, is_active: !c.is_active } : c
      ));
      toast.success(`Customer ${customer.is_active ? 'deactivated' : 'activated'}`);
    } catch {
      toast.error('Failed to update customer');
    }
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;

  return (
    <div className="fcc-fade-in" style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: '600' }}>Manage Customers</h1>
          <p style={{ color: '#5B6B7F', fontSize: '14px' }}>Add, edit, or deactivate customers</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => navigate('/dashboard')} className="fcc-press" style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px', color: '#0F1B2D' }}>
            ← Dashboard
          </button>
          <button onClick={() => setShowModal(true)} className="btn btn-primary fcc-press" style={{ width: 'auto', padding: '8px 16px' }}>
            + New Customer
          </button>
        </div>
      </div>

      {/* Customer list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {customers.map(c => (
          <div key={c.customer_id} className="fcc-hover-lift" style={{
            background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px',
            padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            opacity: c.is_active ? 1 : 0.6
          }}>
            <div>
              <p style={{ fontWeight: '600', fontSize: '15px' }}>{c.name}</p>
              <p style={{ color: '#5B6B7F', fontSize: '13px', marginTop: '2px' }}>
                Agent #{c.agent_number} · {c.is_active ? 'Active' : 'Inactive'}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => navigate(`/admin/settings/${c.customer_id}`)}
                className="fcc-press"
                style={{ background: '#FDF4DD', color: '#F5A800', border: '1px solid #F2D89A', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '13px' }}
              >
                Settings
              </button>
              <button
                onClick={() => navigate(`/admin/zone-rates/${c.customer_id}`)}
                className="fcc-press"
                style={{ background: '#FDF4DD', color: '#F5A800', border: '1px solid #F2D89A', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '13px' }}
              >
                Zone Rates
              </button>
              <button
                onClick={() => navigate(`/admin/zip-data/${c.customer_id}`)}
                className="fcc-press"
                style={{ background: '#FDF4DD', color: '#F5A800', border: '1px solid #F2D89A', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '13px' }}
              >
                ZIP Data
              </button>
              <button
                onClick={() => toggleActive(c)}
                className="fcc-press"
                style={{
                  background: c.is_active ? '#FCEBEB' : '#E9F7EF',
                  color: c.is_active ? '#dc2626' : '#15803D',
                  border: `1px solid ${c.is_active ? '#F5B5B5' : '#A7D9BC'}`,
                  borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '13px'
                }}
              >
                {c.is_active ? 'Deactivate' : 'Activate'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Create customer modal */}
      {showModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div className="fcc-modal-pop" style={{ background: 'white', borderRadius: '12px', padding: '28px', width: '440px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px' }}>New Customer</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Customer Name *</label>
                <input
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: '6px', fontSize: '14px', outline: 'none' }}
                  placeholder="e.g. Home Depot"
                  value={form.name}
                  onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Agent Number</label>
                <input
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: '6px', fontSize: '14px', outline: 'none' }}
                  placeholder="e.g. 20343"
                  value={form.agent_number}
                  onChange={e => setForm(prev => ({ ...prev, agent_number: e.target.value }))}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Copy zone rates from</label>
                <select
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: '6px', fontSize: '14px', outline: 'none' }}
                  value={form.copy_from_customer_id}
                  onChange={e => setForm(prev => ({ ...prev, copy_from_customer_id: e.target.value }))}
                >
                  <option value="">Start from scratch</option>
                  {customers.map(c => (
                    <option key={c.customer_id} value={c.customer_id}>{c.name}</option>
                  ))}
                </select>
                <p style={{ fontSize: '12px', color: '#5B6B7F', marginTop: '4px' }}>
                  {form.copy_from_customer_id
                    ? 'Zone rates and settings will be duplicated from the selected customer'
                    : 'Zone rates will be empty — you will need to add them manually'}
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowModal(false); setForm({ name: '', agent_number: '', copy_from_customer_id: '' }); }}
                className="fcc-press"
                style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '9px 18px', cursor: 'pointer', fontSize: '14px', color: '#0F1B2D' }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={saving}
                className="btn btn-primary fcc-press"
                style={{ width: 'auto', padding: '9px 20px' }}
              >
                {saving ? 'Creating...' : 'Create Customer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerManagement;